import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  Search, Flag, Link2, MessageSquare, ChevronLeft, ChevronRight, ExternalLink, FileText,
  FileCheck2, X,
} from 'lucide-react';
import AppLayout from '../components/AppLayout';
import SyncBar from '../components/SyncBar';
import PetanqueLoader from '../components/PetanqueLoader';
import InvoiceDetailDialog from '../components/InvoiceDetailDialog';
import SortableTableHead from '../components/SortableTableHead';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { api } from '../api/sangly';
import { money, moneyExact, formatNumber, fmtDate, duePhrase, BLOCKED, PRIORITY, PAYMENT } from '../lib/format';
import { cn } from '../lib/utils';
import usePollWhileActive from '../hooks/usePollWhileActive';

const PAGE = 50;
const CURRENT_YEAR = new Date().getFullYear();
const yearStart = () => `${CURRENT_YEAR}-01-01`;
const inDays = (n) => { const d = new Date(); d.setDate(d.getDate() + n); return d.toISOString().slice(0, 10); };

const PRIO_DOT = { critical: 'bg-rose-500', high: 'bg-amber-500', medium: 'bg-primary', low: 'bg-slate-300' };

const FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'open', label: 'Open' },
  { key: 'overdue', label: 'Overdue' },
  { key: 'due_week', label: 'Due ≤ 7d' },
  { key: 'blocked', label: 'Blocked' },
  { key: 'at_risk', label: 'At risk' },
  { key: 'flagged', label: 'Following' },
  { key: 'paid', label: 'Paid' },
];

function StatTile({ label, value, sub, active, tone, onClick }) {
  return (
    <button onClick={onClick}
      className={cn('rounded-lg border bg-card px-4 py-3 text-left shadow-card transition-colors hover:border-primary/40',
        active ? 'border-primary/60 ring-1 ring-primary/30' : 'border-border')}>
      <div className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className={cn('mt-1 text-xl font-semibold tnum', tone)}>{formatNumber(value)}</div>
      {sub && <div className="mt-0.5 text-[11px] text-muted-foreground">{sub}</div>}
    </button>
  );
}

// `unit`: lamptwist or calum — the API scopes rows and tiles to that business;
// a bill with lines for both (mixed) shows in both views.
export default function Invoices({ unit = 'lamptwist' }) {
  const base = unit === 'calum' ? '/calum/invoices' : '/invoices';
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const [filter, setFilter] = useState(searchParams.get('filter') || 'open');
  const [q, setQ] = useState('');
  const [ordering, setOrdering] = useState('priority');
  const [recentOnly, setRecentOnly] = useState(true);    // this year only
  const [billType, setBillType] = useState(searchParams.get('type') || 'all');  // all | supplier | other
  const [amountMin, setAmountMin] = useState('');
  const [amountMax, setAmountMax] = useState('');
  const [offset, setOffset] = useState(0);

  const [rows, setRows] = useState([]);
  const [count, setCount] = useState(0);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [selected, setSelected] = useState(() => new Set());   // invoice ids ticked for bulk actions
  const [bulkLoading, setBulkLoading] = useState(false);
  const [bulkMsg, setBulkMsg] = useState('');

  const params = useMemo(() => {
    const p = { ordering, limit: PAGE, offset };
    p.unit = unit;
    if (q.trim()) p.q = q.trim();
    if (recentOnly) p.invoice_since = yearStart();   // "This year" now scopes All/Paid too
    if (billType !== 'all') p.type = billType;
    if (amountMin !== '') p.amount_min = amountMin;
    if (amountMax !== '') p.amount_max = amountMax;
    switch (filter) {
      case 'all': break;  // no status filter → every invoice, every vendor
      case 'open': p.status = 'open'; break;
      case 'overdue': p.status = 'overdue'; break;
      case 'due_week': p.status = 'open'; p.due_before = inDays(7); break;
      case 'blocked': p.status = 'open'; p.blocked = '3'; break;
      case 'at_risk': p.status = 'open'; p.blocked = '2'; break;
      case 'flagged': p.status = 'flagged'; break;
      case 'paid': p.status = 'paid'; break;
      default: p.status = 'open';
    }
    return p;
  }, [filter, q, ordering, recentOnly, billType, amountMin, amountMax, offset, unit]);

  const load = useCallback(async (soft = false) => {
    if (!soft) setLoading(true);
    try {
      const [data, s] = await Promise.all([api.invoices.list(params), api.invoices.stats({ unit })]);
      setRows(data.results || []);
      setCount(data.count || 0);
      setStats(s);
    } finally { setLoading(false); }
  }, [params, unit]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.users.list().then(setUsers).catch(() => {}); }, []);
  // Live feel: soft-refresh the list every 20s without a spinner.
  usePollWhileActive(true, () => load(true), 20000);

  // reset to first page when filters change
  useEffect(() => { setOffset(0); }, [filter, q, ordering, recentOnly, billType, amountMin, amountMax]);
  // a new page / filter / search means a fresh set of rows - drop the selection
  useEffect(() => { setSelected(new Set()); setBulkMsg(''); }, [params]);

  const openDetail = (invId) => navigate(`${base}/${invId}`);
  const closeDetail = () => navigate(base);

  // ---- bulk selection (for proof-of-payment generation) ----
  const paidSelectedIds = rows.filter((r) => selected.has(r.id) && r.payment_state === 'paid').map((r) => r.id);
  const toggleOne = (id) => setSelected((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const allSelected = rows.length > 0 && rows.every((r) => selected.has(r.id));
  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(rows.map((r) => r.id)));
  const generateProofs = async () => {
    if (!paidSelectedIds.length) return;
    setBulkLoading(true); setBulkMsg('');
    try {
      const { blob, skipped } = await api.invoices.proofs(paidSelectedIds);
      window.open(URL.createObjectURL(blob), '_blank');
      if (skipped) setBulkMsg(`${skipped} skipped — no reconciled payment in Odoo yet`);
    } catch {
      setBulkMsg('Could not generate proofs — are the selected bills paid?');
    } finally { setBulkLoading(false); }
  };

  const from = count === 0 ? 0 : offset + 1;
  const to = Math.min(offset + PAGE, count);

  return (
    <AppLayout
      title={unit === 'calum' ? 'Calum · Invoices' : 'Vendor invoices'}
      description={unit === 'calum'
        ? 'Calum Brussels supplier payments - bills booked on the Calum / Horeca accounts, synced from Odoo'
        : 'Supplier payments, reminders & escalation - synced from Odoo'}
      syncBar={<SyncBar onSynced={() => load(true)} />}
    >
      {/* stat tiles */}
      {stats && (
        <div className="mb-5 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
          <StatTile label="Open bills" value={stats.open_count} sub={money(stats.open_amount)}
                    active={filter === 'open'} onClick={() => setFilter('open')} />
          <StatTile label="Overdue" value={stats.overdue_count} tone="text-rose-600"
                    active={filter === 'overdue'} onClick={() => setFilter('overdue')} />
          <StatTile label="Due ≤ 7 days" value={stats.due_this_week} tone="text-amber-600"
                    active={filter === 'due_week'} onClick={() => setFilter('due_week')} />
          <StatTile label="Account blocked" value={stats.blocked_count} tone="text-rose-600"
                    active={filter === 'blocked'} onClick={() => setFilter('blocked')} />
          <StatTile label="At risk" value={stats.at_risk_count} tone="text-amber-600"
                    active={filter === 'at_risk'} onClick={() => setFilter('at_risk')} />
          <StatTile label="Following" value={stats.flagged_count} tone="text-primary"
                    active={filter === 'flagged'} onClick={() => setFilter('flagged')} />
        </div>
      )}

      {/* toolbar */}
      <div className="mb-3 flex flex-col gap-2 xl:flex-row xl:flex-wrap xl:items-center">
        <div className="flex flex-wrap gap-1.5">
          {FILTERS.map((f) => (
            <button key={f.key} onClick={() => setFilter(f.key)}
              className={cn('rounded-md border px-2.5 py-1 text-[12px] font-medium transition-colors',
                filter === f.key ? 'border-primary bg-primary/12 text-foreground' : 'border-border bg-card text-muted-foreground hover:bg-muted')}>
              {f.label}
            </button>
          ))}
        </div>
        <div className="flex w-full flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center xl:ml-auto xl:w-auto">
          <Select className="h-9 w-full sm:w-36" value={billType} onChange={(e) => setBillType(e.target.value)}
                  title="Supplier bill = the vendor has at least one purchase order">
            <option value="all">Type: All</option>
            <option value="supplier">Supplier bills</option>
            <option value="other">Other (no PO)</option>
          </Select>
          <label className="flex cursor-pointer items-center gap-1.5 text-[12px] text-muted-foreground"
                 title={`Only invoices dated ${CURRENT_YEAR}`}>
            <input type="checkbox" checked={recentOnly} onChange={(e) => setRecentOnly(e.target.checked)}
                   className="h-3.5 w-3.5 rounded border-border accent-[hsl(var(--primary))]" disabled={filter === 'paid'} />
            This year
          </label>
          <div className="flex w-full items-center gap-1 sm:w-auto" title="Filter by invoice amount (€)">
            <Input type="number" inputMode="decimal" value={amountMin} onChange={(e) => setAmountMin(e.target.value)}
                   placeholder="min €" className="h-9 min-w-0 flex-1 px-2 sm:w-20 sm:flex-none" />
            <span className="text-muted-foreground">–</span>
            <Input type="number" inputMode="decimal" value={amountMax} onChange={(e) => setAmountMax(e.target.value)}
                   placeholder="max €" className="h-9 min-w-0 flex-1 px-2 sm:w-20 sm:flex-none" />
          </div>
          <div className="relative">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Bill #, supplier, reference…"
                   className="h-9 w-full pl-8 sm:w-56" />
          </div>
          <Select className="h-9 w-full sm:w-40" value={ordering} onChange={(e) => setOrdering(e.target.value)}>
            <option value="priority">Sort: Priority</option>
            <option value="due">Due date ↑</option>
            <option value="-due">Due date ↓</option>
            <option value="-invoice_amount">Invoice amount ↓</option>
            <option value="invoice_amount">Invoice amount ↑</option>
            <option value="-amount_due">Still to pay ↓</option>
            <option value="amount_due">Still to pay ↑</option>
            <option value="supplier">Supplier A–Z</option>
            <option value="-supplier">Supplier Z–A</option>
          </Select>
        </div>
      </div>

      {/* bulk action bar */}
      {selected.size > 0 && (
        <div className="mb-3 flex flex-wrap items-center gap-x-3 gap-y-2 rounded-lg border border-primary/40 bg-primary/8 px-3 py-2">
          <span className="text-[13px] font-medium">{selected.size} selected</span>
          {paidSelectedIds.length < selected.size && (
            <span className="text-[12px] text-muted-foreground">· {paidSelectedIds.length} paid</span>
          )}
          <Button size="sm" onClick={generateProofs} disabled={bulkLoading || paidSelectedIds.length === 0}
                  title={paidSelectedIds.length === 0 ? 'Only paid bills have a proof of payment' : ''}>
            {bulkLoading ? <PetanqueLoader size={14} /> : <FileCheck2 className="h-4 w-4" />}
            Generate proof{paidSelectedIds.length === 1 ? '' : 's'} of payment
            {paidSelectedIds.length ? ` (${paidSelectedIds.length})` : ''}
          </Button>
          {bulkMsg && <span className="text-[12px] text-muted-foreground">{bulkMsg}</span>}
          <button onClick={() => setSelected(new Set())}
                  className="ml-auto inline-flex items-center gap-1 text-[12px] text-muted-foreground hover:text-foreground">
            <X className="h-3.5 w-3.5" /> Clear
          </button>
        </div>
      )}

      {/* table */}
      <div className="overflow-hidden rounded-lg border border-border bg-card shadow-card">
        <Table className="min-w-[1040px]">
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead className="w-[34px] pr-0">
                <input type="checkbox" aria-label="Select all on this page" checked={allSelected}
                       ref={(el) => { if (el) el.indeterminate = !allSelected && rows.some((r) => selected.has(r.id)); }}
                       onChange={toggleAll}
                       className="h-3.5 w-3.5 cursor-pointer rounded border-border accent-[hsl(var(--primary))]" />
              </TableHead>
              <TableHead className="w-[26px]"></TableHead>
              <SortableTableHead ordering={ordering} onOrderingChange={setOrdering} asc="supplier" desc="-supplier">
                Supplier / invoice
              </SortableTableHead>
              <SortableTableHead ordering={ordering} onOrderingChange={setOrdering} asc="due" desc="-due">
                Due
              </SortableTableHead>
              <SortableTableHead
                className="text-right"
                ordering={ordering}
                onOrderingChange={setOrdering}
                asc="invoice_amount"
                desc="-invoice_amount"
                defaultDirection="desc"
              >
                Invoice amount
              </SortableTableHead>
              <SortableTableHead
                className="text-right"
                ordering={ordering}
                onOrderingChange={setOrdering}
                asc="amount_due"
                desc="-amount_due"
                defaultDirection="desc"
              >
                Still to pay
              </SortableTableHead>
              <SortableTableHead
                ordering={ordering}
                onOrderingChange={setOrdering}
                asc="account"
                desc="-account"
                defaultDirection="desc"
              >
                Account
              </SortableTableHead>
              <TableHead className="text-right">Flags</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && (
              <TableRow><TableCell colSpan={8} className="h-40 text-center">
                <PetanqueLoader size={34} roll className="mx-auto" />
              </TableCell></TableRow>
            )}
            {!loading && rows.length === 0 && (
              <TableRow><TableCell colSpan={8} className="h-40 text-center text-muted-foreground">
                No invoices match these filters.
              </TableCell></TableRow>
            )}
            {!loading && rows.map((inv) => {
              const blk = BLOCKED[inv.account_blocked] || BLOCKED[1];
              return (
                <TableRow key={inv.id}
                          className={cn('cursor-pointer', selected.has(inv.id) && 'bg-primary/5')}
                          onClick={() => openDetail(inv.id)}>
                  <TableCell className="pr-0" onClick={(e) => e.stopPropagation()}>
                    <input type="checkbox" aria-label={`Select ${inv.name || 'invoice'}`}
                           checked={selected.has(inv.id)} onChange={() => toggleOne(inv.id)}
                           className="h-3.5 w-3.5 cursor-pointer rounded border-border accent-[hsl(var(--primary))]" />
                  </TableCell>
                  <TableCell className="pr-0">
                    <span className={cn('block h-6 w-1 rounded-full', PRIO_DOT[inv.priority_label] || 'bg-slate-300')}
                          title={`${inv.priority_label} priority`} />
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1.5 leading-tight">
                      <span className="font-medium">{inv.supplier || '-'}</span>
                      <Badge variant={inv.has_po ? 'accent' : 'outline'} className="text-[10px]">
                        {inv.has_po ? 'Supplier' : 'Other'}
                      </Badge>
                      {inv.business_unit === 'mixed' && <Badge variant="outline" className="text-[10px]" title="Lines for both LampTwist and Calum — shown in both views">Mixed</Badge>}
                    </div>
                    <div className="text-[11px] text-muted-foreground">
                      <span className="font-mono">{inv.name || '-'}</span>
                      {inv.ref && <> · ref <span className="font-mono text-foreground/70">{inv.ref}</span></>}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className={cn('leading-tight', inv.is_overdue && 'text-rose-600')}>{fmtDate(inv.date_due)}</div>
                    <div className={cn('text-[11px]', inv.is_overdue ? 'text-rose-500' : 'text-muted-foreground')}>
                      {inv.payment_state === 'paid' ? 'paid' : duePhrase(inv.days_overdue)}
                    </div>
                  </TableCell>
                  <TableCell className="text-right tnum text-muted-foreground" title={moneyExact(inv.amount_total, inv.currency)}>
                    {money(inv.amount_total, inv.currency)}
                  </TableCell>
                  <TableCell className="text-right font-semibold tnum" title={moneyExact(inv.amount_residual, inv.currency)}>
                    {money(inv.amount_residual, inv.currency)}
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-wrap items-center gap-1">
                      <Badge variant={blk.variant}>{blk.label}</Badge>
                      {inv.payment_state !== 'not_paid' && (
                        <Badge variant={(PAYMENT[inv.payment_state] || PAYMENT.not_paid).variant}>
                          {(PAYMENT[inv.payment_state] || PAYMENT.not_paid).label}
                        </Badge>
                      )}
                    </div>
                    {inv.payment_date && (inv.payment_state === 'in_payment' || inv.payment_state === 'paid') && (
                      <div className="mt-0.5 text-[11px] text-muted-foreground">
                        {inv.payment_state === 'in_payment' ? 'planned ' : 'paid '}{fmtDate(inv.payment_date)}
                      </div>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center justify-end gap-2 text-muted-foreground">
                      {inv.is_flagged && <Flag className="h-3.5 w-3.5 text-primary" title={`Following · ${inv.flagged_by_name}`} />}
                      {inv.gorgias_url && <Link2 className="h-3.5 w-3.5" title="Gorgias ticket linked" />}
                      {inv.comment_count > 0 && (
                        <span className="inline-flex items-center gap-0.5 text-[11px]"><MessageSquare className="h-3.5 w-3.5" />{inv.comment_count}</span>
                      )}
                      {inv.pdf_url && (
                        <a href={inv.pdf_url} target="_blank" rel="noreferrer"
                           onClick={(e) => e.stopPropagation()}
                           className="rounded p-0.5 transition-colors hover:bg-muted hover:text-foreground"
                           title="Open supplier invoice PDF">
                          <FileText className="h-3.5 w-3.5" />
                        </a>
                      )}
                      {inv.odoo_url && (
                        <a href={inv.odoo_url} target="_blank" rel="noreferrer"
                           onClick={(e) => e.stopPropagation()}
                           className="rounded p-0.5 transition-colors hover:bg-muted hover:text-foreground"
                           title="Open in Odoo">
                          <ExternalLink className="h-3.5 w-3.5" />
                        </a>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      {/* pagination */}
      <div className="mt-3 flex items-center justify-between text-[12px] text-muted-foreground">
        <span>{from}–{to} of {count.toLocaleString('nl-BE')}</span>
        <div className="flex items-center gap-1">
          <button disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - PAGE))}
            className="inline-flex h-8 items-center gap-1 rounded-md border border-border bg-card px-2 disabled:opacity-40 hover:bg-muted">
            <ChevronLeft className="h-4 w-4" /> Prev
          </button>
          <button disabled={to >= count} onClick={() => setOffset(offset + PAGE)}
            className="inline-flex h-8 items-center gap-1 rounded-md border border-border bg-card px-2 disabled:opacity-40 hover:bg-muted">
            Next <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      </div>

      <InvoiceDetailDialog invoiceId={id} open={!!id} onClose={closeDetail}
                           onChanged={() => load(true)} users={users} />
    </AppLayout>
  );
}
